// The "Chat with Document" scope a dialogue saved, restored into the chat
// store on load. The store does not persist it across reloads, and without it
// the chat stops being document-scoped: getActiveDatasets then counts the
// documents' datasets as selected, and search unions them in.
import { QueryClient } from "react-query";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { getSourceDataRecord } from "src/views/datasets/services/DatasetServices";

// Record ids from a dialogue's saved config; [] when absent or malformed.
export const savedDocumentIds = (config: any): number[] => {
  const ids = config?.source_data_record_ids;
  return Array.isArray(ids) ? ids.filter(Number.isInteger) : [];
};

const DOCUMENT_CHIP_PREFIX = "sdr-";

// The attachment chip a Chat with Document entry point adds for a record.
export const documentAttachment = (record: any): FileUploadState => ({
  id: `${DOCUMENT_CHIP_PREFIX}${record.id}`,
  file: {
    name: record.data?.file_name || `Document ${record.id}`,
    size: 0,
    type: "application/pdf",
  } as File,
  datasetId: record.dataset_fk ?? undefined,
  sourceDataRecordId: record.id,
  uploadStatus: "uploaded",
  uploadProgress: 100,
  processingStatus: "completed",
  processingProgress: 100,
});

// Chat with Document chips for records outside `keepIds`. Opening a chat from
// history does not reset the store, so a previous chat's chips survive into it.
export const staleDocumentChipIds = (
  files: FileUploadState[],
  keepIds: number[],
): string[] => {
  const keep = new Set(keepIds);
  return files
    .filter(
      (f) =>
        f.id.startsWith(DOCUMENT_CHIP_PREFIX) &&
        !keep.has(Number(f.sourceDataRecordId)),
    )
    .map((f) => f.id);
};

// Kept fresh this long so reopening a dialogue reuses the record it just read.
const RECORD_STALE_MS = 60_000;

// Chips for the records that can still be read; a failed read gets no chip.
// Reads share the ["sourceDataRecord", id] cache with useGetSourceDataRecord.
export const fetchDocumentAttachments = async (
  ids: number[],
  queryClient: QueryClient,
): Promise<FileUploadState[]> => {
  const records = await Promise.all(
    ids.map((id) =>
      queryClient
        .fetchQuery(["sourceDataRecord", id], () => getSourceDataRecord(id), {
          staleTime: RECORD_STALE_MS,
        })
        .catch(() => null),
    ),
  );
  return records.filter(Boolean).map(documentAttachment);
};
